import React, { useState, useEffect, useMemo } from "react";
import sampleJson from "./sample.json";
import { buildQuestionList } from "./SurveyBuilder";
import Language from "./components/Languages";
import LogoImage from "assets/images/Logo blue.png";
import styled from "styled-components";
import { applyQuestionVisibilityLogic } from "./skipLogic"; // <-- import here


import SurveyLoader from "./components/SurveyLoader";
import SurveyInvalidToken from "./components/SurveyStates/SurveyInvalidToken";
import SurveyAlreadyCompleted from "./components/SurveyStates/SurveyAlreadyCompleted";
import SurveyPartialData from "./components/SurveyStates/SurveyPartialData";
import SurveyError from "./components/SurveyStates/SurveyError";
import SurveyComplete from "./components/SurveyStates/SurveyComplete";
import SurveyQuestions from "./components/SurveyQuestions";



export default function MySurveyWrapper() {
  // Core survey state
  const [questionList, setQuestionList] = useState([]);
  const [answers, setAnswers] = useState({});
  const [language, setLanguage] = useState("EN");
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [endScreens, setEndScreens] = useState([]);
  const [currentEndScreenIndex, setCurrentEndScreenIndex] = useState(0);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState("Loading survey...");
  const [loadingError, setLoadingError] = useState(null);
  const [hasPartialData, setHasPartialData] = useState(false);
  const [savedAnswers, setSavedAnswers] = useState({});
  const [tokenValid, setTokenValid] = useState(true);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  // Initialize language from URL param on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const langParam = params.get('language');
      if (!langParam) return;
      if (langParam === '0' || langParam.toUpperCase() === 'EN') {
        setLanguage('EN');
      } else if (langParam === '1' || langParam.toUpperCase() === 'FR') {
        setLanguage('FR');
      } else if (langParam === '2' || langParam.toUpperCase() === 'ES') {
        setLanguage('ES');
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Define the theme colors
  const themeColors = {
    buttonColor: sampleJson?.themeSettings?.buttonColor || '#007bff',
    highlightText: sampleJson?.themeSettings?.highlightText || '#333',
    topBanner: sampleJson?.themeSettings?.topBanner || '#f8f9fa',
    secondaryButton: sampleJson?.themeSettings?.secondaryButton || '#6c757d',
    backgroundColor: sampleJson?.themeSettings?.backgroundColor || '#ffffff',
  }

  // Build a list of available language codes from sampleJson
  const allLanguages = useMemo(() => {
    const codesFromJson = Array.isArray(sampleJson.languageCodes)
      ? sampleJson.languageCodes
      : [];
    const combined = ["EN", ...codesFromJson];
    const unique = [...new Set(combined)]; // remove duplicates if any
    return unique;
  }, []);

  // Effect to load survey data
  useEffect(() => {
    let isMounted = true;
    
    async function loadSurveyData() {
      try {
        setLoadingStatus("Loading survey data...");
        
        // Simulate a network request
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        if (!isMounted) return;
        
        // Always make the token valid for simulation purposes
        // Comment out or modify the original token validation logic
        /*
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        
        if (!token) {
          setTokenValid(false);
          setLoadingError("Invalid or missing survey token");
          setLoading(false);
          return;
        }
        */
        
        // Force token to be valid
        setTokenValid(true);
        
        // Simulate checking completion status
        setLoadingStatus("Checking survey status...");
        await new Promise(resolve => setTimeout(resolve, 800));
        
        if (!isMounted) return;
        
        // SIMULATION: Create mock partial data
        const mockPartialAnswers = {
          "q1": "Previously saved answer for question 1",
          "q2": "Previously saved answer for question 2",
          "q3": ["Option A", "Option C"]
        };
        
        setSavedAnswers(mockPartialAnswers);
        setHasPartialData(true);
        setLoading(false);
        
        // Don't load questions yet - that happens after the user chooses whether
        // to continue or start fresh
        
      } catch (error) {
        console.error("Failed to load survey:", error);
        if (isMounted) {
          setLoadingError("Failed to load survey data. Please try again later.");
          setLoading(false);
        }
      }
    }
    
    loadSurveyData();
    
    return () => {
      isMounted = false;
    };
  }, [language]);

  // Apply skip logic whenever questionList or answers change
  useEffect(() => {
    if (questionList.length === 0) {
      setVisibleQuestions([]);
      return;
    }
    
    const updated = applyQuestionVisibilityLogic(
      questionList,
      answers,
      sampleJson.factors,
      sampleJson.aggregateFactors
    );
    
    const onlyVisible = updated.filter((q) => !q.skip);
    setVisibleQuestions(onlyVisible);
    
    if (currentQuestionIndex >= onlyVisible.length) {
      setCurrentQuestionIndex(onlyVisible.length - 1);
    }
  }, [questionList, answers, currentQuestionIndex]);

  // Function to handle answer changes
  function handleAnswerChange(uniqueId, value) {
    setAnswers((prev) => ({
      ...prev,
      [uniqueId]: value,
    }));
  }

  // Function to simulate survey completion - super simple version
  const simulateCompletion = () => {
    // Just mark as completed without trying to set any answers
    setCompleted(true);
  };

  // Function to handle continuing with saved data
  const handleContinueSavedData = () => {
    setAnswers(savedAnswers);
    setHasPartialData(false);
    
    const allQuestions = buildQuestionList(sampleJson, language);
    setQuestionList(allQuestions);
    
    const endScreenItems = Array.isArray(sampleJson.endScreenItems) && sampleJson.endScreenItems.length > 0
      ? sampleJson.endScreenItems
      : [];
    setEndScreens(endScreenItems);
  };

  // Function to handle starting fresh
  const handleStartFresh = () => {
    setHasPartialData(false);
    
    const allQuestions = buildQuestionList(sampleJson, language);
    setQuestionList(allQuestions);
    
    const endScreenItems = Array.isArray(sampleJson.endScreenItems) && sampleJson.endScreenItems.length > 0
      ? sampleJson.endScreenItems
      : [];
    setEndScreens(endScreenItems);
  };

  // Conditional rendering based on current state
  if (loading) {
    return <SurveyLoader status={loadingStatus} themeColors={themeColors} />;
  }

  if (!tokenValid) {
    return <SurveyInvalidToken themeColors={themeColors} />;
  }

  if (alreadyCompleted) {
    return <SurveyAlreadyCompleted themeColors={themeColors} />;
  }

  if (hasPartialData) {
    return (
      <SurveyPartialData 
        themeColors={themeColors}
        onContinue={handleContinueSavedData}
        onRestart={handleStartFresh}
        logo={LogoImage}
      />
    );
  }

  if (loadingError) {
    return (
      <SurveyError 
        themeColors={themeColors}
        errorMessage={loadingError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (completed) {
    return (
      <SurveyComplete
        themeColors={themeColors}
        endScreens={endScreens}
        currentEndScreenIndex={currentEndScreenIndex}
        setCurrentEndScreenIndex={setCurrentEndScreenIndex}
        logo={LogoImage}
        language={language}
        setLanguage={setLanguage}
        allLanguages={allLanguages}
      />
    );
  }

  // All other states display the main survey
  return (
    <>
      
      {visibleQuestions && visibleQuestions.length > 0 ? (
        <SurveyQuestions
          themeColors={themeColors}
          visibleQuestions={visibleQuestions}
          currentQuestionIndex={currentQuestionIndex}
          setCurrentQuestionIndex={setCurrentQuestionIndex}
          answers={answers}
          handleAnswerChange={handleAnswerChange}
          setCompleted={setCompleted}
          language={language}
          setLanguage={setLanguage}
          logo={LogoImage}
          sampleJson={sampleJson}
        />
      ) : (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          marginTop: '50px'
        }}>
          <h2>No questions available</h2>
          <p>There seems to be an issue loading the survey questions.</p>
          <button 
            onClick={simulateCompletion}
            style={{
              padding: '10px 15px',
              backgroundColor: themeColors.buttonColor,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              marginTop: '20px',
              cursor: 'pointer'
            }}
          >
            Skip to Completion
          </button>
        </div>
      )}
    </>
  );
}

// Styled component for the simulation button
const SimulateCompletionButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 10px 15px;
  background-color: ${props => props.themeColors.secondaryButton};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  z-index: 1000;
  font-weight: bold;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  
  &:hover {
    background-color: #5a6268;
  }
`;
